import { type KeyboardEventHandler, type Ref, useMemo, useRef } from 'react';

import { type FieldKey, useDebounce, useMinWidth } from 'hooks';
import { usePopularProducts } from 'hooks/data-fetching';
import type { InspirationLink, MainLinkFields } from 'models/sitecore';
import type { TemplateString } from 'types';
import { cn } from 'utils/classNames';
import { filterTruthy } from 'utils/collection';
import { is } from 'utils/helpers';

import { getLinkPath, getStripeGradient, isLargeDropdown } from './helpers';
import PageHeaderInspirationLinks from './PageHeaderInspirationLinks';
import PageHeaderInspirationMedia from './PageHeaderInspirationMedia';
import PageHeaderLinkList, {
	type LinkListTabHandler,
} from './PageHeaderLinkList';
import type { DropdownLinkClickHandler } from './PageHeaderLinkListItem';
import PopularProductsList from './PopularProductsList';
import PopularSearchTerms from './PopularSearchTerms';

interface Props {
	/** A unique ID for this dropdown */
	id: string;

	inspirationLink: InspirationLink | undefined;
	inspirationLinksHeading: string;
	inspirationMediaHeading: string;

	/** If the user is on a large screen */
	isLargeScreen: boolean | undefined;

	/** If currently open */
	isOpen?: boolean;

	/** The full link tree */
	links: MainLinkFields[];

	navigationCategoryShowAllButton: TemplateString | FieldKey;

	/* Tab key press handler for the last link in the menu when large screen dropdowns are used */
	onLargeScreenMenuLastLinkTab: KeyboardEventHandler<HTMLAnchorElement>;

	/* Click handler for links on all levels */
	onLinkClick?: DropdownLinkClickHandler;

	/* Tab key press handler for the last link in the menu when it's toggled for small screens */
	onToggledMenuLastLinkTab: KeyboardEventHandler<HTMLAnchorElement>;

	popularProductsText: string;

	ref?: Ref<HTMLDivElement>;
	searchPopularSearchTermsText: string;

	/** Currently selected link inside the dropdown, if any */
	selectedLinkId: string;

	/** Heading text */
	title?: string;
}

/** Dropdown menu content */
export default function PageHeaderDropdown({
	id,
	inspirationLink,
	inspirationLinksHeading,
	inspirationMediaHeading,
	isLargeScreen,
	isOpen = false,
	links,
	navigationCategoryShowAllButton,
	onLargeScreenMenuLastLinkTab,
	onLinkClick,
	onToggledMenuLastLinkTab,
	popularProductsText,
	ref,
	searchPopularSearchTermsText,
	selectedLinkId,
	title,
}: Props) {
	const selected = useMemo(
		() => getLinkPath(selectedLinkId, links),
		[selectedLinkId, links],
	);
	const openLevels = selected.length + 1;
	// The objects are for each tree level.
	const linkRefs = [
		{
			selected: useRef<HTMLAnchorElement>(null),
			selectedSibling: useRef<HTMLAnchorElement>(null),
		},
		{
			first: useRef<HTMLAnchorElement>(null),
			selected: useRef<HTMLAnchorElement>(null),
			selectedSibling: useRef<HTMLAnchorElement>(null),
		},
		{
			first: useRef<HTMLAnchorElement>(null),
		},
	];

	const handleLinkClick: DropdownLinkClickHandler = (linkId) => {
		onLinkClick?.(linkId);
	};

	const handleSelectedLinkTab: LinkListTabHandler = (level, event) => {
		// Tab to the first link in the expanded child list.
		if (isLargeScreen && !event.shiftKey) {
			// Level = index + 1 for the next level.
			// If the first one is selected, the first ref won't be set.
			const firstChild =
				linkRefs[level]?.first?.current ?? linkRefs[level]?.selected?.current;
			if (firstChild) {
				event.preventDefault();
				firstChild.focus();
			}
		}
	};

	const handleFirstLinkTab: LinkListTabHandler = (level, event) => {
		// Tab back to the selected item in the parent list.
		if (isLargeScreen && event.shiftKey) {
			// Level - 1 for index, - 1 again for previous level.
			const selectedParent = linkRefs[level - 2]?.selected?.current;
			if (selectedParent) {
				event.preventDefault();
				selectedParent.focus();
			}
		}
	};

	const handleLastLinkTab: LinkListTabHandler = (level, event) => {
		if (event.shiftKey) {
			return;
		}
		if (isLargeScreen) {
			const hasSelected = selected.length > 0;
			const isLastLinkOnLevel1Selected =
				links.findIndex((link) => link.id === selected[0]?.id) ===
				links.length - 1;
			// If any sub levels are open, the native focus would move into those
			// and the next if branch would move it back to the parent, causing
			// focus to be stuck in a loop inside the dropdown.
			if (
				hasSelected &&
				// Tab on final top level link when it's not selected. The selected
				// link handler will handle it otherwise.
				((level === 1 && !isLastLinkOnLevel1Selected) ||
					// Tab on nested link when the final top level link is selected.
					(level === 2 && isLastLinkOnLevel1Selected))
			) {
				// The callback should move focus out of the dropdown.
				onLargeScreenMenuLastLinkTab(event);
			} else if (level > 1) {
				// Tab back to the link following the selected one in the parent list.
				// Level - 1 for index, - 1 again for previous level.
				const selectedParentSibling =
					linkRefs[level - 2]?.selectedSibling?.current;
				if (selectedParentSibling) {
					event.preventDefault();
					selectedParentSibling.focus();
				}
			}
		} else {
			onToggledMenuLastLinkTab(event);
		}
	};

	const isLargePanel = isLargeDropdown(links);
	const hasPopular = isOpen && isLargePanel;
	const linkTheme = selected[0]?.color;

	// Use debouncing for some data to take the small screen back button
	// transition into account. Otherwise props like `links` sent to
	// PageHeaderLinkList will be empty right when the back button is pressed,
	// causing the list of links to disappear while the back transition runs.
	// This makes it look like a flash of disappearing content and the menu
	// feels a bit janky.
	const transitionDuration = 150;
	const selectedDebounced = useDebounce(selected, transitionDuration);
	const isOpenDebounced = useDebounce(isOpen, transitionDuration);
	const getSelectedLink = (index: number): MainLinkFields | undefined =>
		selected[index] ?? selectedDebounced[index];

	const isMinLg = useMinWidth('lg');
	const pageHeaderInspirationLinks = selected[0]?.contentLinks;
	const hasVisiblePageHeaderInspirationLinks =
		is.arrayWithLength(pageHeaderInspirationLinks) && isMinLg;

	const { data: popularProducts } = usePopularProducts({
		// Don't trigger fetching when hidden.
		isActive: hasPopular,
		categoryId: openLevels < 3 ? selectedLinkId : undefined,
	});
	const hasVisibleLevel2PopularContent =
		openLevels === 2 &&
		(is.arrayWithLength(popularProducts) ||
			hasVisiblePageHeaderInspirationLinks);

	const contentColumnsBaseClasses =
		'absolute top-12 z-30 col-span-4 w-full px-6 lg:col-span-3';

	// On large screens the dropdown will be full width if there's more than
	// one level of links, otherwise it's a small list centered under the
	// trigger link.
	// On small screens each dropdown level is stacked on top of each other
	// inside the toggled menu panel.
	return (
		<div
			ref={ref}
			id={id}
			className={cn(
				'absolute z-10 w-full bg-base',
				// For forced colors mode
				'before:pointer-events-none before:absolute before:inset-0 before:z-50 before:border-x before:border-b before:border-transparent',
				!isOpen && 'invisible',
				!isLargeScreen && [
					'top-0 bottom-0 overflow-hidden transition-[visibility,opacity,translate] duration-150',
					!isOpen && 'translate-x-5 opacity-0 delay-[150ms,0s,0s]',
				],
				isLargeScreen && [
					'top-full mt-px shadow-2xl',
					isLargePanel && 'left-0 grid h-auto grid-cols-12 gap-x-6',
					!isLargePanel && 'left-1/2 w-auto min-w-60 -translate-x-1/2',
				],
			)}
		>
			{(isOpen || isOpenDebounced) && (
				// The container is invisible when closed so the content isn't in the
				// way, but any headings inside are disliked for SEO reasons so don't
				// render anything when closed to avoid having it included in the
				// server response.
				<>
					{isLargePanel && (
						<div
							className="h-2 forced-color-adjust-none max-md:hidden md:col-span-12"
							style={{
								background:
									linkTheme ||
									getStripeGradient(
										filterTruthy(links, 'color')
											// White stops against a white background looks broken.
											.filter(
												(link) => !['#fff', '#ffffff'].includes(link.color),
											)
											.map((link) => link.color),
									),
							}}
						/>
					)}
					<PageHeaderLinkList
						level={1}
						className="md:col-span-3"
						baseId={id}
						isLargeScreen={isLargeScreen}
						openLevels={openLevels}
						links={links}
						navigationCategoryShowAllButton={navigationCategoryShowAllButton}
						onLastLinkTab={handleLastLinkTab}
						onLinkClick={isLargePanel ? handleLinkClick : undefined}
						onSelectedLinkTab={handleSelectedLinkTab}
						renderPopularSearchTerms={hasPopular && !isLargeScreen}
						searchPopularSearchTermsText={searchPopularSearchTermsText}
						selectedLink={selected[0]}
						selectedLinkRef={linkRefs[0]?.selected}
						selectedSiblingLinkRef={linkRefs[0]?.selectedSibling}
						title={isLargePanel ? title : undefined}
					/>
					{isLargePanel && (
						<>
							<PageHeaderLinkList
								level={2}
								className="md:col-span-3"
								baseId={id}
								parentId={selected[0]?.id}
								firstLinkRef={linkRefs[1]?.first}
								isLargeScreen={isLargeScreen}
								openLevels={openLevels}
								linkBarColor={linkTheme}
								links={getSelectedLink(0)?.links}
								navigationCategoryShowAllButton={
									navigationCategoryShowAllButton
								}
								onFirstLinkTab={handleFirstLinkTab}
								onLastLinkTab={handleLastLinkTab}
								hasPopularContent={hasVisibleLevel2PopularContent}
								onLinkClick={handleLinkClick}
								onSelectedLinkTab={handleSelectedLinkTab}
								searchPopularSearchTermsText={searchPopularSearchTermsText}
								selectedLink={selected[1]}
								selectedLinkRef={linkRefs[1]?.selected}
								selectedSiblingLinkRef={linkRefs[1]?.selectedSibling}
								title={getSelectedLink(0)?.text}
								viewAllHref={getSelectedLink(0)?.href}
							/>
							<PageHeaderLinkList
								level={3}
								className="md:col-span-6"
								baseId={id}
								parentId={selected[1]?.id}
								firstLinkRef={linkRefs[2]?.first}
								isLargeScreen={isLargeScreen}
								openLevels={openLevels}
								linkBarColor={linkTheme}
								links={getSelectedLink(1)?.links}
								navigationCategoryShowAllButton={
									navigationCategoryShowAllButton
								}
								onFirstLinkTab={handleFirstLinkTab}
								onLastLinkTab={handleLastLinkTab}
								searchPopularSearchTermsText={searchPopularSearchTermsText}
								title={getSelectedLink(1)?.text}
								viewAllHref={getSelectedLink(1)?.href}
							/>
						</>
					)}
					{hasPopular && isLargeScreen && (
						<>
							<PopularProductsList
								heading={popularProductsText}
								products={popularProducts}
								onLastLinkTab={
									openLevels === 2 && !hasVisiblePageHeaderInspirationLinks
										? (e) => handleLastLinkTab(2, e)
										: undefined
								}
								className={cn(
									contentColumnsBaseClasses,
									openLevels === 1 && 'col-start-4!',
									openLevels === 2 && 'col-start-7!',
									openLevels === 3 && 'hidden!',
								)}
							/>
							<PageHeaderInspirationMedia
								inspirationLink={inspirationLink}
								heading={inspirationMediaHeading}
								className={cn(
									contentColumnsBaseClasses,
									'col-start-8 lg:col-start-7',
									(openLevels === 2 || openLevels === 3) && 'hidden!',
								)}
							/>
							<PopularSearchTerms
								heading={searchPopularSearchTermsText}
								variant="chips"
								itemLimit={5}
								className={cn(
									contentColumnsBaseClasses,
									(openLevels === 2 || openLevels === 3) && 'hidden!',
									inspirationLink
										? 'max-lg:hidden lg:col-start-10'
										: 'col-start-8 lg:col-start-7',
								)}
							/>
							{openLevels === 2 && hasVisiblePageHeaderInspirationLinks && (
								<PageHeaderInspirationLinks
									inspirationLinksHeading={inspirationLinksHeading}
									links={pageHeaderInspirationLinks}
									onLastLinkTab={(e) => handleLastLinkTab(2, e)}
									className={cn(
										contentColumnsBaseClasses,
										'max-lg:hidden lg:col-start-10',
									)}
								/>
							)}
						</>
					)}
				</>
			)}
		</div>
	);
}
PageHeaderDropdown.displayName = 'PageHeaderDropdown';
